import { ArrowLeft, CircleAlert, CircleCheck, LoaderCircle, LogIn, Mail, MailCheck } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { RESET_LINK_MINUTES, authErrorMessage, requestPasswordReset, signIn } from '../lib/authClient';
import { Dialog } from './Dialog';
import { PasswordField } from './PasswordField';

export type SignInView = 'sign-in' | 'forgot' | 'forgot-sent';

export interface SignInDialogProps {
  open: boolean;
  initialView?: SignInView;
  /** Success message shown above the sign-in form (e.g. after setting a new password). */
  notice?: string | undefined;
  onClose: () => void;
  onSignedIn: (email: string) => void;
}

export function SignInDialog({ open, initialView = 'sign-in', notice, onClose, onSignedIn }: SignInDialogProps) {
  const [view, setView] = useState<SignInView>(initialView);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const emailId = useId();
  const errorId = useId();

  useEffect(() => {
    if (open) {
      setView(initialView);
      setError(null);
      setPassword('');
    }
  }, [open, initialView]);

  const go = (next: SignInView) => {
    setError(null);
    setView(next);
  };

  const submitSignIn = async (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !password) {
      setError('Bitte gib E-Mail-Adresse und Passwort ein.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
      onSignedIn(email.trim());
    } catch (err) {
      setError(authErrorMessage(err, 'sign-in'));
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (event: FormEvent) => {
    event.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError('Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email.trim());
      go('forgot-sent');
    } catch (err) {
      setError(authErrorMessage(err, 'request-reset'));
    } finally {
      setBusy(false);
    }
  };

  const errorBox = error && (
    <p id={errorId} className="inline-error" role="alert">
      <CircleAlert size={18} aria-hidden="true" />
      {error}
    </p>
  );

  const emailField = (
    <div className="field">
      <label htmlFor={emailId} className="field__label">
        E-Mail-Adresse
      </label>
      <input
        id={emailId}
        className="field__input"
        type="email"
        autoComplete="username"
        inputMode="email"
        autoCapitalize="none"
        spellCheck={false}
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        required
      />
    </div>
  );

  const titles: Record<SignInView, string> = {
    'sign-in': 'Anmelden',
    forgot: 'Passwort vergessen?',
    'forgot-sent': 'Schau in dein Postfach',
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title={titles[view]}
      icon={view === 'sign-in' ? <LogIn size={22} /> : <Mail size={22} />}
      description={
        view === 'sign-in'
          ? notice
            ? undefined
            : 'Mit einem Konto wird deine ChatGPT-Verbindung gespeichert und Transkripte werden auf dem Server geladen.'
          : view === 'forgot'
            ? 'Wir schicken dir einen Link, mit dem du ein neues Passwort festlegen kannst.'
            : undefined
      }
    >
      {view === 'sign-in' && (
        <form className="form" onSubmit={submitSignIn} noValidate>
          {notice && (
            <p className="inline-success" role="status">
              <CircleCheck size={18} aria-hidden="true" />
              {notice}
            </p>
          )}
          {emailField}
          <PasswordField
            label="Passwort"
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
            invalid={!!error}
            describedBy={error ? errorId : undefined}
          />
          {errorBox}
          <div className="form-actions form-actions--split">
            <button type="button" className="link-btn" onClick={() => go('forgot')}>
              Passwort vergessen?
            </button>
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy && <LoaderCircle size={18} className="spin" aria-hidden="true" />}
              Anmelden
            </button>
          </div>
          <p className="dialog__footnote dialog__footnote--plain">
            Konten werden vom Betreiber angelegt – eine Registrierung gibt es nicht. Neu dabei? Lege dein Passwort über
            „Passwort vergessen?“ fest. Ohne Konto funktioniert die App auch.
          </p>
        </form>
      )}

      {view === 'forgot' && (
        <form className="form" onSubmit={submitForgot} noValidate>
          {emailField}
          {errorBox}
          <div className="form-actions form-actions--split">
            <button type="button" className="link-btn" onClick={() => go('sign-in')}>
              <ArrowLeft size={16} aria-hidden="true" />
              Zurück
            </button>
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy && <LoaderCircle size={18} className="spin" aria-hidden="true" />}
              Link senden
            </button>
          </div>
        </form>
      )}

      {view === 'forgot-sent' && (
        <div className="form sent">
          <span className="sent__icon" aria-hidden="true">
            <MailCheck size={28} />
          </span>
          <p role="status">
            Falls es ein Konto für <strong>{email.trim()}</strong> gibt, ist eine E-Mail mit einem Link zum Festlegen
            eines neuen Passworts unterwegs. Der Link ist {RESET_LINK_MINUTES} Minuten gültig – schau auch im
            Spam-Ordner nach.
          </p>
          <div className="form-actions">
            <button type="button" className="btn btn--secondary" onClick={() => go('sign-in')} data-autofocus>
              Zurück zur Anmeldung
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
